/**
 * features/exams/ExamDaysPane.tsx — the session about to be run, then the rest.
 * ------------------------------------------------------------------
 * The screen the exam officer opens on the morning of an exam. It leads with
 * ONE session — the next one that has not finished — because that is the
 * question being asked at 07:10, and a list of thirty-one sessions answers it
 * only after scrolling.
 *
 * Two numbers that are not the same, and are labelled so nobody adds them up:
 * CANDIDATES is bodies in the building (a pupil sitting two papers in one
 * session is one desk), SCRIPTS is papers to hand out and collect.
 */
import { useMemo } from 'react';
import { CalendarDays, Clock, Users } from 'lucide-react';
import { SoftCard, StatChip, StatStrip } from '@/components/ui/design';
import type { ExamSessionLoad } from '@/lib/examTimetable';
import { gaboroneInstant, sessionName } from '@/lib/examTimetable';
import type { ExamRunData } from './ExamsHub';
import { shortDay } from './ExamsHub';

export default function ExamDaysPane({ run, now }: { run: ExamRunData; now: number }) {
  const { loads, loading } = run;

  const { next, later, done } = useMemo(() => {
    const ended = (l: ExamSessionLoad) => gaboroneInstant(l.date, l.window ? '23:59' : l.end || '23:59') < now;
    const remaining = loads.filter((l) => !ended(l));
    return { next: remaining[0] ?? null, later: remaining.slice(1), done: loads.filter(ended).length };
  }, [loads, now]);

  if (loading) return <p className="py-10 text-sm text-[var(--sp-muted)]">Loading the exam days…</p>;
  if (!loads.length) {
    return (
      <SoftCard pad="">
        <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">
          No candidate entries have been imported for this series yet, so there are no exam days to run.
        </p>
      </SoftCard>
    );
  }

  const scripts = next ? next.papers.reduce((n, p) => n + p.candidates, 0) : 0;

  return (
    <div className="space-y-5">
      <StatStrip>
        <StatChip
          tone="navy" icon={CalendarDays}
          value={next ? shortDay(next.date) : '—'}
          label={next ? (next.window ? 'next — test window' : `next — ${next.session} ${next.start}`) : 'the series is over'}
        />
        <StatChip tone="sky" icon={Users} value={next?.candidates ?? 0} label="candidates in the building" />
        <StatChip tone="amber" value={scripts} label="scripts to hand out" />
        <StatChip tone="slate" value={loads.length - done} label={`sessions left of ${loads.length}`} />
      </StatStrip>

      {next && (
        <SoftCard pad="">
          <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-[var(--sp-line)] px-5 py-3">
            <h2 className="text-base font-bold text-ink dark:text-slate-100">
              {shortDay(next.date)}
              {next.window ? ' — test window' : `, ${next.session ? sessionName(next.session).toLowerCase() : ''} session`}
            </h2>
            <p className="inline-flex items-center gap-1.5 text-sm text-[var(--sp-muted)]">
              <Clock className="h-3.5 w-3.5" />
              {next.window ? 'the school sets the time' : `${next.start} – ${next.end}`}
            </p>
          </header>
          <PaperTable load={next} />
        </SoftCard>
      )}

      <SoftCard pad="">
        <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-[var(--sp-line)] px-5 py-3">
          <h2 className="text-base font-bold text-ink dark:text-slate-100">What is coming</h2>
          <p className="text-xs text-[var(--sp-muted)]">{later.length} more sessions · {done} finished</p>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--sp-line)] text-left text-[11px] uppercase tracking-wide text-[var(--sp-muted)]">
                <th className="px-5 py-2 font-semibold">Day</th>
                <th className="px-3 py-2 font-semibold">Session</th>
                <th className="px-3 py-2 font-semibold">Papers</th>
                <th className="px-3 py-2 text-right font-semibold">Candidates</th>
              </tr>
            </thead>
            <tbody>
              {later.map((l) => (
                <tr key={l.key} className="border-b border-[var(--sp-line)]/60 last:border-0">
                  <td className="whitespace-nowrap px-5 py-2.5 font-semibold text-ink dark:text-slate-100">
                    <CalendarDays className="mr-1.5 inline h-3.5 w-3.5 text-[var(--sp-muted)]" aria-hidden />
                    {shortDay(l.date)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-[var(--sp-body)]">
                    {l.window ? 'test window' : `${l.session} · ${l.start}`}
                  </td>
                  <td className="px-3 py-2.5 text-[var(--sp-body)]">
                    {l.papers.map((p) => `${p.code} ${p.subject}${p.ownTimes ? ` (${p.start}–${p.end})` : ''}`).join(', ')}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--sp-body)]">{l.candidates}</td>
                </tr>
              ))}
              {!later.length && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-[var(--sp-muted)]">
                  Nothing else to sit — every session of this series has finished.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SoftCard>
    </div>
  );
}

function PaperTable({ load }: { load: ExamSessionLoad }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--sp-line)] text-left text-[11px] uppercase tracking-wide text-[var(--sp-muted)]">
            <th className="px-5 py-2 font-semibold">Paper</th>
            <th className="px-3 py-2 font-semibold">Classes</th>
            <th className="px-3 py-2 text-right font-semibold">Scripts</th>
            <th className="px-3 py-2 text-right font-semibold">Length</th>
          </tr>
        </thead>
        <tbody>
          {load.papers.map((p) => (
            <tr key={p.code} className="border-b border-[var(--sp-line)]/60 last:border-0">
              <td className="px-5 py-2.5">
                <span className="font-semibold text-ink dark:text-slate-100">{p.code}</span>{' '}
                <span className="text-[var(--sp-body)]">{p.subject}</span>
                {p.title && <span className="text-[var(--sp-muted)]"> · {p.title}</span>}
                {p.ownTimes && <span className="ml-1 text-xs font-semibold text-primary"> {p.start}–{p.end}</span>}
              </td>
              <td className="px-3 py-2.5 text-[var(--sp-body)]">{p.classes.join(', ') || '—'}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[var(--sp-body)]">{p.candidates}</td>
              <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-[var(--sp-muted)]">
                {p.minutes ? `${p.minutes} min` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--sp-line)] bg-[var(--sp-paper)]">
            <td className="px-5 py-2.5 text-xs text-[var(--sp-muted)]" colSpan={2}>
              <Users className="mr-1.5 inline h-3.5 w-3.5" aria-hidden />
              {load.candidates} candidates — a pupil sitting two papers this session needs one desk, not two.
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-ink dark:text-slate-100">
              {load.papers.reduce((n, p) => n + p.candidates, 0)}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
